import numeral from "numeral"
import { SERIE_FORMAT } from "../feature/chart/ChartOptions"
import { formatTime } from "./date"
import { canUseNumeral } from "./numerals"
type Value = string | number | undefined | null

type FormatterParams = {
  value: Value
  /** The Numeral.js format string to apply (See http://numeraljs.com/#format) */
  format?: string
  prefix?: string
}

export function percentageFormatter({
  value,
  format = "full",
  prefix = "",
}: FormatterParams): string {
  if (value === null || value === undefined) {
    return ""
  }
  if (format === "full") {
    return `${prefix}${numeral(value).format("0,0.0")}%`
  } else {
    return `${prefix}${numeral(numberFormatter({ value, format })).format("0,0.0")}%`
  }
}

export function ratioFormatter({ value, prefix }: FormatterParams) {
  if (value === null || value === undefined) {
    return ""
  }
  return `${prefix || ""}${numeral(value).format("0,0.0")}x`
}

export function numberFormatter({
  value,
  format,
  prefix = "",
}: FormatterParams) {
  if (value === null || value === undefined) {
    return ""
  }

  let compactFormat = "0.0 a"
  let fullFormat = "0,0"

  // For small values, we want to show 2 decimal places
  if (Number(value) < 1) {
    compactFormat = `0.00[00] a`
    fullFormat = `0.00[00]`
  }

  if (format === "compact") {
    return numeral(value).format(`${prefix}${compactFormat}`).toUpperCase()
  } else if (format === "consistent") {
    const valueInThousands = Number(value) / 1000
    const format = valueInThousands < 10 ? `${prefix}0,0.000` : `${prefix}0,0`
    return numeral(valueInThousands).format(format) + " K"
  } else if (format === "full") {
    return numeral(value).format(`${prefix}${fullFormat}`).toUpperCase()
  }

  return numeral(value).format(format).toUpperCase()
}

export function bytesFormatter({ value, prefix }: FormatterParams) {
  if (value === null || value === undefined) {
    return ""
  }
  return `${prefix || ""}${numeral(value).format("0[.]0 b")}`
}

export function timeFormatter({ value, prefix }: FormatterParams) {
  if (typeof value === "undefined" || value === null) {
    return "-"
  }
  if (typeof value === "string") {
    // Should never be :thinking:
    return `${prefix || ""}${formatTime(Number.parseInt(value))}`
  }
  return `${prefix || ""}${formatTime(value)}`
}

function pricingFormatterWithDecimals({
  value,
  format,
  prefix,
}: FormatterParams) {
  if (!value) {
    return `0`
  }
  if (canUseNumeral(value)) {
    if (format) {
      return numeral(value).format(format)
    }

    return numeral(value).format(
      Number(value) > 0.01 ? "$0.00 a" : "$0,0.00[00] a"
    )
  }
  return `${prefix || "$"}${value}`
}

export const formatters = {
  [SERIE_FORMAT.PRICE]: ({ value, format, prefix }: FormatterParams) => {
    if (format === "compact" || format === "full" || format === "consistent") {
      return numberFormatter({ value, format, prefix: prefix || "$" })
    }
    return pricingFormatterWithDecimals({ value, format, prefix })
  },
  [SERIE_FORMAT.RATIO]: ratioFormatter,
  [SERIE_FORMAT.PERCENTAGE]: percentageFormatter,
  [SERIE_FORMAT.NUMBER]: numberFormatter,
  [SERIE_FORMAT.TOKEN]: numberFormatter,
  [SERIE_FORMAT.TIME]: timeFormatter,
  [SERIE_FORMAT.BYTES]: bytesFormatter,
  [SERIE_FORMAT.TOKEN_ICP]: numberFormatter,
  [SERIE_FORMAT.TOKEN_CKBTC]: numberFormatter,
  [SERIE_FORMAT.TOKEN_CKETH]: numberFormatter,
  [SERIE_FORMAT.CYCLES_PER_SECONDS]: numberFormatter,
  [SERIE_FORMAT.KWH]: numberFormatter,
} as const

export function formatNumber({
  value,
  format,
  metricFormat,
  prefix,
}: {
  value: Value
  format?: "compact" | "full" | "consistent"
  metricFormat?: string
  prefix?: string
}) {
  const formatter = formatters[metricFormat as SERIE_FORMAT]
  if (formatter) {
    return formatter({ value, format, prefix })
  }
  return numberFormatter({ value, format, prefix })
}
